import { useState } from "react";
import { CorporationsEntity } from "../../../../entities/Corporations/CorporationsEntity";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import { MainTable } from "../../../../components";
import { IMainTableColumn } from "../../../../components/MainTable/MainTableInterfaces";
import { CorporationForm } from "./CorporationForm/CorporationForm.view";
import { useStyles } from "./Corporations.styles";
import AddIcon from "@mui/icons-material/Add";
import { MADERA_BACKOFFICE_PATHS, USERTYPE } from "../../../../constants/constants";
import { MillForm } from "./MillForm/MillForm.view";
import { Step, StepLabel, Stepper } from "@mui/material";
import { ICorporateReponse } from "./CorporationForm/CorporationFormInterfaces";
import { ICorporation, StepsEnum } from "./CorporationsAPIInterfaces";
import { ModuleLicensesEntity } from "entities/ModuleLicenses/ModuleLicensesEntity";
import { useNavigate } from "react-router-dom";
import useCorporationsColumns from "./hooks/useCorporationsColumns/useCorporationsColumns";

export const Corporations = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [creating, setCreating] = useState<boolean>(false);
    const navigate = useNavigate();
    const { loading, corporations, apolloQueryError, refetch } = CorporationsEntity.GetCorporations(
        USERTYPE.ADMINISTRATOR,
    );
    const { moduleLicenses } = ModuleLicensesEntity.GetModuleLicenses();
    const [activeStep, setActiveStep] = useState(StepsEnum.corporationSetup);
    const [newCorporation, setNewCorporation] = useState<ICorporateReponse | undefined>(undefined);
    const [corporationEditData, setCorporationEditData] = useState<ICorporation | undefined>(undefined);
    const tableColumns = useCorporationsColumns(
        moduleLicenses,
        refetch,
        setCorporationEditData,
    ) as unknown as IMainTableColumn[];

    const handleOpenForm = () => {
        setCreating(true);
        setActiveStep(StepsEnum.corporationSetup);
    };

    const handleCloseForm = async () => {
        setCreating(false);
        setCorporationEditData(undefined);
        await refetch();
    };

    const handleNextStep = (corporation: ICorporateReponse) => {
        setNewCorporation(corporation);
        setActiveStep(StepsEnum.millSetup);
    };

    const steps = [
        {
            id: StepsEnum.corporationSetup,
            label: t("setup_corporation.label_steps.add_corporation"),
        },
        {
            id: StepsEnum.millSetup,
            label: t("setup_corporation.label_steps.add_mill"),
        },
    ];

    const stepsComponents = {
        [StepsEnum.corporationSetup]: {
            component: <CorporationForm closeForm={handleCloseForm} nextStep={handleNextStep} />,
        },
        [StepsEnum.millSetup]: {
            component: <MillForm corporation={newCorporation && newCorporation} closeForm={handleCloseForm} />,
        },
    };

    return !creating && !corporationEditData ? (
        <>
            <div className={classes.container}>
                <h2 className={classes.title}>{t("setup_corporation.table_title")}</h2>
                <Button variant="contained" className={classes.addButton} onClick={handleOpenForm}>
                    <AddIcon fontSize="small" style={{ marginRight: "5px" }} />
                    {t("setup_corporation.add_corporation_button")}
                </Button>
            </div>
            <MainTable
                columns={tableColumns}
                rows={!loading && !apolloQueryError ? corporations : []}
                loading={loading}
                onRowClick={(row: { corporate_id?: number }) => {
                    navigate(
                        `/${MADERA_BACKOFFICE_PATHS.ROOT_PATH}/${MADERA_BACKOFFICE_PATHS.SETUP_DASHBOARD.CORPORATION.ROOT}/${row.corporate_id}`,
                    );
                }}
                tableCellCustomStyle={classes.tableRow}
                pagination
            />
        </>
    ) : creating ? (
        <div className={classes.contentWrapper}>
            <Stepper activeStep={activeStep}>
                {steps.map((step) => (
                    <Step key={step.id} className={classes.step}>
                        <StepLabel>{step.label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
            {stepsComponents[activeStep].component}
        </div>
    ) : (
        <CorporationForm
            corporationEditData={corporationEditData}
            closeForm={handleCloseForm}
            nextStep={handleCloseForm}
        />
    );
};
